import { FC } from 'react'

import { SettingsPageLayout } from '../components/SettingsPageLayout'
import {
  Button,
  EmptyState,
  GreyWhite,
  IconBank,
  IconMapPin,
  IconPlus,
  IndigoIndigo60,
  Inspector,
  Link,
  SettingsFormControl,
  SettingsFormControlIcon,
  SettingsGroup,
  Switch,
  useNotifications
} from '@sequencehq/core-components'

import { useMutation, useQuery } from '@sequencehq/api'

import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { BankDetailsCard } from 'SettingsV2/components/bank-details/BankDetailsCard'
import { ActivityLogsInspectorItem } from 'ActivityLogs/index'
import { paymentDetailsDiffFn } from 'SettingsV2/components/bank-details/paymentDetailsDiff'

const BANK_DETAILS_FIELDS = new Set([
  'bankAccountDetails',
  'includeBeneficiaryAddressInPaymentDetails'
])

export const BankDetailsSettingsPage: FC = () => {
  const { data, isError, isLoading } = useQuery(
    dashboard20240730Client.getMerchantForSequenceAccount
  )

  const { displayNotification } = useNotifications()

  const merchantMutator = useMutation(dashboard20240730Client.putMerchant, {
    onSuccess() {
      displayNotification('Bank details updated', {
        type: 'success'
      })
    },
    onError() {
      displayNotification('Bank details failed to update', {
        type: 'error'
      })
    }
  })

  return (
    <SettingsPageLayout
      header="Bank details"
      subheader="Bank details are displayed on invoices to customers."
      isError={isError}
      actions={
        <Button
          as={Link.Unstyled}
          to="/settings/bank-details/new"
          variant="primary"
          data-testid="settings.bankDetails.new"
          leadingIcon={<IconPlus color={GreyWhite} />}
        >
          New bank details
        </Button>
      }
      inspector={
        data && (
          <Inspector showContentOnMount={false}>
            <Inspector.ItemGroup>
              <ActivityLogsInspectorItem
                enabled
                entityId={data.id}
                entityType="MERCHANT"
                filterFields={BANK_DETAILS_FIELDS}
                itemTitle="Bank details updated"
                diffFn={paymentDetailsDiffFn}
              />
            </Inspector.ItemGroup>
          </Inspector>
        )
      }
    >
      <SettingsGroup isLoading={isLoading}>
        {data?.bankAccountDetails?.map(bankDetails => (
          <BankDetailsCard key={bankDetails.key} bankDetails={bankDetails} />
        ))}

        {!data?.bankAccountDetails?.length && (
          <EmptyState
            title="No bank details"
            icon={IconBank}
            iconSize="sm"
            body={
              <>
                Add details for different currencies such as ACH, Wire or
                regular bank transfers,{' '}
                <Link
                  _hover={{ textDecoration: 'none' }}
                  to="https://docs.sequencehq.com/resources/currencies"
                  color={IndigoIndigo60}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  learn more
                </Link>
                .
              </>
            }
          />
        )}
      </SettingsGroup>

      <SettingsGroup isLoading={isLoading}>
        <SettingsFormControl
          label="Include beneficiary address"
          helper="Include beneficiary address in bank details"
          htmlFor="include-beneficiary-address"
          icon={
            <SettingsFormControlIcon>
              <IconMapPin />
            </SettingsFormControlIcon>
          }
        >
          <Switch
            id="include-beneficiary-address"
            as="div"
            size="md"
            disabled={merchantMutator.isPending}
            defaultChecked={data?.includeBeneficiaryAddressInPaymentDetails}
            onChange={(value: boolean) => {
              if (!data) return
              merchantMutator.mutate({
                id: data.id,
                body: {
                  address: data.address,
                  email: data.email,
                  legalCompanyName: data.legalCompanyName,
                  bankAccountDetails: data.bankAccountDetails,
                  includeBeneficiaryAddressInPaymentDetails: value
                }
              })
            }}
          />
        </SettingsFormControl>
      </SettingsGroup>
    </SettingsPageLayout>
  )
}
